(() => {
  const cartChangeUrl = '/cart/change.js';
  const cartAddUrl = '/cart/add.js';

  // Match the theme's money format (e.g. "Rs. 1,599.00") used by Quick Add.
  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
    style: 'currency', currency: currency || 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(cents / 100).replace('₹', 'Rs. ');

  // Same icons as assets/icon-close.svg, icon-minus.svg and icon-plus.svg.
  const iconClose = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const iconMinus = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/></svg>';
  const iconPlus = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/><path d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/></svg>';

  class CartItemEditDialog {
    constructor() {
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'cart-item-edit-dialog';
      this.dialog.innerHTML = '<div class="cart-item-edit-dialog__panel"></div>';
      document.body.appendChild(this.dialog);
      this.panel = this.dialog.querySelector('.cart-item-edit-dialog__panel');
      this.productRequests = new Map();
      this.comboPageRequests = new Map();
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
    }

    async open(trigger) {
      const requestId = (this.openRequestId || 0) + 1;
      this.openRequestId = requestId;
      this.item = {
        key: trigger.dataset.lineKey,
        line: Number(trigger.dataset.line),
        handle: trigger.dataset.productHandle,
        variantId: Number(trigger.dataset.variantId),
        quantity: Math.max(1, Number(trigger.dataset.quantity) || 1),
        image: trigger.dataset.image,
        title: trigger.dataset.title,
        properties: this.parseProperties(trigger.dataset.properties),
      };
      // Read Combo values directly from the rendered cart line too. This is
      // more reliable than serialising Liquid's properties drop in browsers.
      if (trigger.dataset.topSize) this.item.properties['Top Size'] = trigger.dataset.topSize;
      if (trigger.dataset.bottomSize) this.item.properties['Bottom Size'] = trigger.dataset.bottomSize;
      if (trigger.dataset.topVariantId) this.item.properties['_Top Variant ID'] = trigger.dataset.topVariantId;
      if (trigger.dataset.bottomVariantId) this.item.properties['_Bottom Variant ID'] = trigger.dataset.bottomVariantId;
      // Use the same color scheme as the Quick Add popup.
      this.dialog.className = `cart-item-edit-dialog ${trigger.dataset.colorScheme || ''}`.trim();
      this.panel.innerHTML = '<div class="cart-item-edit-dialog__loading">Loading product…</div>';
      this.dialog.showModal();
      try {
        // These two requests are independent. Starting them together removes
        // a full network wait from every first-time cart edit.
        const [product, combo] = await Promise.all([
          this.loadProduct(this.item.handle),
          this.loadCombo(this.item),
        ]);
        if (requestId !== this.openRequestId || !this.dialog.open) return;
        this.product = product;
        this.selected = this.product.variants.find((variant) => Number(variant.id) === this.item.variantId) || this.product.variants[0];
        this.selectedValues = [...this.variantValues(this.selected)];
        this.combo = combo;
        this.render();
      } catch (error) {
        if (requestId !== this.openRequestId || !this.dialog.open) return;
        this.panel.innerHTML = `<div class="cart-item-edit-dialog__error">${error.message}</div>`;
      }
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    parseProperties(value) {
      try {
        const parsed = JSON.parse(value || '{}');
        if (!Array.isArray(parsed)) return parsed && typeof parsed === 'object' ? parsed : {};
        return parsed.reduce((properties, entry) => {
          if (Array.isArray(entry) && entry.length >= 2) properties[entry[0]] = entry[1];
          else if (entry && typeof entry === 'object') properties[entry.first ?? entry.key] = entry.last ?? entry.value;
          return properties;
        }, {});
      } catch { return {}; }
    }

    variantValues(variant) {
      return variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
    }

    productPageUrl(handle) {
      const url = new URL(`/products/${encodeURIComponent(handle)}`, window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);

      // A cart editor opened from Shopify's theme preview must read the
      // previewed product template too. Without these parameters, this fetch
      // silently reads the published theme and can use the wrong linked
      // Top/Bottom products or stock state.
      ['preview_theme_id', '_fd', 'pb'].forEach((name) => {
        if (currentParams.has(name)) url.searchParams.set(name, currentParams.get(name));
      });

      return url.toString();
    }

    loadProduct(handle) {
      const key = String(handle);
      if (!this.productRequests.has(key)) {
        const request = fetch(`/products/${encodeURIComponent(handle)}.js`)
          .then((response) => {
            if (!response.ok) throw new Error('Could not load this product.');
            return response.json();
          })
          .catch((error) => {
            this.productRequests.delete(key);
            throw error;
          });
        this.productRequests.set(key, request);
      }
      return this.productRequests.get(key);
    }

    loadComboPage(handle) {
      const url = this.productPageUrl(handle);
      if (!this.comboPageRequests.has(url)) {
        const request = fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error('Could not load the combo picker.');
            return response.text();
          })
          .catch((error) => {
            this.comboPageRequests.delete(url);
            throw error;
          });
        this.comboPageRequests.set(url, request);
      }
      return this.comboPageRequests.get(url);
    }

    preload(trigger) {
      const handle = trigger?.dataset?.productHandle;
      if (!handle) return;
      this.loadProduct(handle).catch(() => {});
      if (trigger.dataset.topVariantId && trigger.dataset.bottomVariantId) {
        this.loadComboPage(handle).catch(() => {});
      }
    }

    async loadCombo(item) {
      if (!item.properties['_Top Variant ID'] || !item.properties['_Bottom Variant ID']) return null;
      try {
        const html = new DOMParser().parseFromString(await this.loadComboPage(item.handle), 'text/html');
        const picker = html.querySelector('combo-variant-picker');
        if (!picker) return null;
        const groups = Array.from(picker.querySelectorAll('[data-combo-group]')).map((element) => {
          const key = element.dataset.comboGroup;
          const label = key === 'top' ? (picker.dataset.topLabel || 'Top') : (picker.dataset.bottomLabel || 'Bottom');
          const variants = JSON.parse(element.querySelector('script[data-combo-variants]')?.textContent || '[]');
          const selectedId = Number(item.properties[key === 'top' ? '_Top Variant ID' : '_Bottom Variant ID']);
          const savedSize = item.properties[`${label} Size`] || item.properties[`${key === 'top' ? 'Top' : 'Bottom'} Size`];
          // Size properties are the customer-facing cart state. Prefer them over
          // a stale private linked-variant ID left by an earlier cart update.
          const selected = variants.find((variant) => this.variantValues(variant).filter(Boolean).join(' / ') === savedSize)
            || variants.find((variant) => Number(variant.id) === selectedId)
            || variants.find((variant) => variant.available)
            || variants[0];
          const optionElements = Array.from(element.querySelectorAll('.variant-option'));
          const labels = optionElements.map((option) => (option.querySelector('legend, label')?.textContent || '')
            .trim().replace(/\s+/g, ' '));
          // Keep the exact catalog order rendered by combo-option-group.liquid.
          // Shopify's variants JSON may be returned with the selected variant first.
          const optionInputs = optionElements.map((option) => Array.from(
            option.querySelectorAll('input[type="radio"], option')
          ));
          const optionValues = optionInputs.map((inputs) => [...new Set(
            inputs.map((input) => input.value).filter(Boolean)
          )]);
          // Read availability from the original combo picker markup. This is
          // the theme's authoritative per-size stock state and, unlike a
          // calculated variant candidate, does not change when the customer
          // temporarily selects another size in the editor.
          const availableOptionValues = optionInputs.map((inputs) => [...new Set(
            inputs
              .filter((input) => input.getAttribute('aria-disabled') !== 'true')
              .map((input) => input.value)
              .filter(Boolean)
          )]);
          return {
            key,
            label,
            variants,
            labels,
            optionValues,
            availableOptionValues,
            selectedValues: this.variantValues(selected),
            // A customer must always be able to return to the variant that is
            // already on this cart line. Shopify can report that variant as
            // unavailable after it has been added to the cart, which should
            // not make a temporary size change irreversible in this editor.
            originalVariantId: Number(selected.id),
          };
        });
        return groups.length === 2 ? { groups } : null;
      } catch {
        return null;
      }
    }

    comboVariant(group) {
      return group.variants.find((variant) => this.variantValues(variant).every((value, index) => value === group.selectedValues[index]));
    }

    comboVariantIsSelectable(group, variant) {
      return Boolean(variant && (variant.available || Number(variant.id) === group.originalVariantId));
    }

    comboOptionIsSelectable(group, optionIndex, value, candidate) {
      const availableValues = group.availableOptionValues?.[optionIndex];
      if (availableValues?.length) return availableValues.includes(value);
      return this.comboVariantIsSelectable(group, candidate);
    }

    comboMarkup() {
      if (!this.combo) return '';
      return this.combo.groups.map((group) => {
        const optionCount = this.variantValues(group.variants[0] || {}).length;
        const options = Array.from({ length: optionCount }, (_, optionIndex) => {
          const values = group.optionValues?.[optionIndex]?.length
            ? group.optionValues[optionIndex]
            : [...new Set(group.variants.map((variant) => this.variantValues(variant)[optionIndex]).filter(Boolean))];
          const label = group.labels[optionIndex] || (optionCount === 1 ? 'Size' : `Option ${optionIndex + 1}`);
          // Like the Quick Add combo picker, a single option is labelled by the
          // group heading alone ("Top" / "Bottom"), without a second "Size" legend.
          const legendClass = optionCount === 1 ? ' class="visually-hidden"' : '';
          return `<fieldset class="cart-item-edit-dialog__option"><legend${legendClass}>${this.escape(label)}</legend><div class="cart-item-edit-dialog__values">${values.map((value) => {
            const proposed = [...group.selectedValues]; proposed[optionIndex] = value;
            const candidate = group.variants.find((variant) => this.variantValues(variant).every((variantValue, index) => variantValue === proposed[index]));
            const active = group.selectedValues[optionIndex] === value;
            const unavailable = !this.comboOptionIsSelectable(group, optionIndex, value, candidate);
            return `<button type="button" class="cart-item-edit-dialog__value${active ? ' is-selected' : ''}" data-combo-group="${group.key}" data-option-index="${optionIndex}" data-option-value="${this.escapeAttribute(value)}"${unavailable ? ' disabled aria-disabled="true"' : ''}>${this.escape(value)}</button>`;
          }).join('')}</div></fieldset>`;
        }).join('');
        return `<section class="cart-item-edit-dialog__combo-group"><p class="cart-item-edit-dialog__group-label">${this.escape(group.label)}</p>${options}</section>`;
      }).join('');
    }

    currentVariant() {
      return this.product.variants.find((variant) => this.variantValues(variant).every((value, index) => value === this.selectedValues[index]));
    }

    render() {
      const variant = this.currentVariant() || this.selected;
      this.selected = variant;
      const options = this.product.options || [];
      // Like Quick Add, show every product image in a scrollable column,
      // starting with the selected variant's image.
      const firstImage = variant.featured_image?.src || this.item.image || this.product.featured_image;
      const productImages = (this.product.media || [])
        .filter((media) => media.media_type === 'image' && media.src)
        .map((media) => media.src);
      const images = [firstImage, ...(productImages.length ? productImages : this.product.images || [])]
        .filter(Boolean)
        .filter((src, index, list) => list.findIndex((other) => this.imageKey(other) === this.imageKey(src)) === index);
      const galleryScroll = this.panel.querySelector('.cart-item-edit-dialog__image')?.scrollTop || 0;
      const isDefaultOption = options.length === 1 && (options[0] === 'Title' || options[0]?.name === 'Title');
      const optionMarkup = options.length && !isDefaultOption
        ? options.map((optionDefinition, optionIndex) => {
          const option = typeof optionDefinition === 'string' ? optionDefinition : optionDefinition.name;
          const values = [...new Set(this.product.variants.map((entry) => this.variantValues(entry)[optionIndex]).filter(Boolean))];
          return `<fieldset class="cart-item-edit-dialog__option"><legend>${this.escape(option)}</legend><div class="cart-item-edit-dialog__values">${values.map((value) => {
            const proposed = [...this.selectedValues]; proposed[optionIndex] = value;
            const candidate = this.product.variants.find((entry) => this.variantValues(entry).every((entryValue, index) => entryValue === proposed[index]));
            const active = this.selectedValues[optionIndex] === value;
            return `<button type="button" class="cart-item-edit-dialog__value${active ? ' is-selected' : ''}" data-option-index="${optionIndex}" data-option-value="${this.escapeAttribute(value)}"${candidate?.available ? '' : ' disabled'}>${this.escape(value)}</button>`;
          }).join('')}</div></fieldset>`;
        }).join('')
        : '';
      this.panel.innerHTML = `
        <button type="button" class="cart-item-edit-dialog__close" aria-label="Close">${iconClose}</button>
        <div class="cart-item-edit-dialog__image"><div class="cart-item-edit-dialog__gallery">${images.map((src, index) => `<img src="${this.escapeAttribute(this.sizedImage(src))}" alt="" ${index ? 'loading="lazy"' : ''}>`).join('')}</div></div>
        <div class="cart-item-edit-dialog__details">
          <div class="cart-item-edit-dialog__info">
            <h2>${this.escape(this.product.title || this.item.title)}</h2>
            <p class="cart-item-edit-dialog__price">${money(variant.price, this.currency())}</p>
            ${variant.compare_at_price && variant.compare_at_price > variant.price ? `<s class="cart-item-edit-dialog__compare-price">${money(variant.compare_at_price, this.currency())}</s>` : ''}
            <p class="cart-item-edit-dialog__tax-note">Shipping calculated at checkout.</p>
          </div>
          <div class="cart-item-edit-dialog__options">${optionMarkup}${this.comboMarkup()}</div>
          <div class="cart-item-edit-dialog__footer">
            <div class="cart-item-edit-dialog__quantity" aria-label="Quantity"><button type="button" data-quantity="decrease" aria-label="Decrease quantity">${iconMinus}</button><span>${this.item.quantity}</span><button type="button" data-quantity="increase" aria-label="Increase quantity">${iconPlus}</button></div>
            <button type="button" class="button button-primary cart-item-edit-dialog__submit"${variant.available && this.comboIsValid() ? '' : ' disabled'}>${variant.available && this.comboIsValid() ? 'Update cart' : 'Selected size unavailable'}</button>
          </div>
          <p class="cart-item-edit-dialog__message" aria-live="polite"></p>
        </div>`;
      // Keep the gallery where the customer left it when a size is clicked.
      const gallery = this.panel.querySelector('.cart-item-edit-dialog__image');
      if (gallery) gallery.scrollTop = galleryScroll;
      this.bind();
    }

    bind() {
      this.panel.querySelector('.cart-item-edit-dialog__close').addEventListener('click', () => this.close());
      this.panel.querySelectorAll('[data-option-index]').forEach((button) => button.addEventListener('click', () => {
        if (button.dataset.comboGroup) {
          const group = this.combo?.groups.find((entry) => entry.key === button.dataset.comboGroup);
          if (group) group.selectedValues[Number(button.dataset.optionIndex)] = button.dataset.optionValue;
          this.render();
          return;
        }
        this.selectedValues[Number(button.dataset.optionIndex)] = button.dataset.optionValue;
        this.render();
      }));
      this.panel.querySelectorAll('[data-quantity]').forEach((button) => button.addEventListener('click', () => {
        this.item.quantity = Math.max(1, this.item.quantity + (button.dataset.quantity === 'increase' ? 1 : -1));
        this.render();
      }));
      this.panel.querySelector('.cart-item-edit-dialog__submit')?.addEventListener('click', () => this.update());
    }

    async update() {
      const submit = this.panel.querySelector('.cart-item-edit-dialog__submit');
      const message = this.panel.querySelector('.cart-item-edit-dialog__message');
      const variant = this.currentVariant();
      if (!variant?.available || !this.comboIsValid() || !submit) return;
      submit.disabled = true;
      submit.textContent = 'Updating…';
      try {
        const sections = Array.from(document.querySelectorAll('cart-items-component')).map((element) => element.dataset.sectionId).filter(Boolean);
        const properties = this.updatedProperties();
        let response;
        if (Number(variant.id) === this.item.variantId) {
          response = await this.cartChange({ key: this.item.key, quantity: this.item.quantity, properties, sections });
        } else {
          await this.cartAdd({ id: Number(variant.id), quantity: this.item.quantity, properties, sections });
          // Adding a different variant can reorder cart rows. The original
          // line key remains the only reliable target for removing the old
          // size, whereas a numeric line position can point at the new item.
          response = await this.cartChange({ key: this.item.key, quantity: 0, sections });
        }
        this.notifyCart(response, sections);
        this.close();
      } catch (error) {
        message.textContent = error.message || 'Could not update your cart. Please try again.';
        submit.disabled = false;
        submit.textContent = 'Update cart';
      }
    }

    updatedProperties() {
      const properties = { ...this.item.properties };
      if (!this.combo) return properties;
      this.combo.groups.forEach((group) => {
        const variant = this.comboVariant(group);
        if (!variant) return;
        const prefix = group.key === 'top' ? 'Top' : 'Bottom';
        properties[`${group.label} Size`] = this.variantValues(variant).filter(Boolean).join(' / ');
        properties[`_${prefix} Variant ID`] = String(variant.id);
      });
      return properties;
    }

    comboIsValid() {
      return !this.combo || this.combo.groups.every((group) => {
        const variant = this.comboVariant(group);
        return variant && group.selectedValues.every((value, optionIndex) => (
          this.comboOptionIsSelectable(group, optionIndex, value, variant)
        ));
      });
    }

    async cartAdd(body) {
      const response = await fetch(cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: body.id, quantity: body.quantity, properties: body.properties }], sections: body.sections.join(',') }) });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not update your cart.');
      return data;
    }

    async cartChange({ key, line, quantity, properties, sections }) {
      const body = { quantity, sections: sections.join(','), sections_url: window.location.pathname };
      if (key) body.id = key;
      else body.line = line;
      if (properties) body.properties = properties;
      const response = await fetch(cartChangeUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || data.errors || 'Could not update your cart.');
      return data;
    }

    notifyCart(data, sections) {
      const sectionId = sections[0];
      const html = sectionId && data.sections?.[sectionId];
      const count = html ? new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent : null;
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: { source: 'cart-item-edit', itemCount: Number(count) || 0, sections: data.sections || {}, didError: false } } }));
    }

    imageKey(src) { return String(src).replace(/^https?:/, '').split('?')[0]; }
    sizedImage(src) {
      const url = String(src);
      return /[?&]width=/.test(url) ? url : `${url}${url.includes('?') ? '&' : '?'}width=800`;
    }

    currency() { return window.Shopify?.currency?.active || 'INR'; }
    escape(value) { const element = document.createElement('div'); element.textContent = value ?? ''; return element.innerHTML; }
    escapeAttribute(value) { return this.escape(value).replace(/`/g, '&#96;'); }
  }

  let editor;
  const getEditor = () => (editor ??= new CartItemEditDialog());

  // Desktop starts loading as the cursor reaches the icon. Pointer down gives
  // touch devices the same head start before the following click opens it.
  ['pointerover', 'pointerdown'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-cart-edit]') : null;
      if (button) getEditor().preload(button);
    }, { passive: true });
  });

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-cart-edit]') : null;
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    getEditor().open(button);
  });
})();
